import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  Container,
  Collapse,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  fetchAgencies,
  fetchAgencyMemberships,
  updateAgencyMembership,
  fetchAllowedCertifications,
  fetchCertificationsByUserIds,
  fetchRegistrationsForUser,
  fetchClassById,
} from "../../api/apiClient";
import { canPickAgency, defaultAgencyId } from "./agencySelection";
import { useTenant } from "../../contexts/TenantContext";
import { useActiveTenant } from "../../hooks/useActiveTenant";
import NoTenantSelected from "../../components/NoTenantSelected";

// ── helpers ───────────────────────────────────────────────────────────────

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return Math.ceil((d - now) / 86400000);
}

function certStatus(cert) {
  if (!cert) return "missing";
  const days = daysUntil(cert.expires);
  if (days === null) return "missing";
  if (days < 0) return "expired";
  if (days <= 30) return "expiring";
  return "valid";
}

const STATUS_CFG = {
  valid: { color: "success", label: "Valid" },
  expiring: { color: "warning", label: "Expiring Soon" },
  expired: { color: "error", label: "Expired" },
  missing: { color: "default", label: "Not on File" },
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString();
}

function findCertForType(myCerts, certType) {
  return (
    myCerts.find((c) => c.certTypeId === certType.id) ||
    myCerts.find((c) => c.name === certType.name) ||
    null
  );
}

// ── Main export ────────────────────────────────────────────────────────────────

export default function AgencyMemberCompliance() {
  const { user, isFeatureEnabled } = useTenant();
  const { tenantId: activeTenantId } = useActiveTenant();
  const navigate = useNavigate();
  const [agencies, setAgencies] = useState([]);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [members, setMembers] = useState([]);
  const [approvedMemberships, setApprovedMemberships] = useState([]);
  const [certsByUser, setCertsByUser] = useState({});
  const [, setPendingRequests] = useState([]);
  const [classesByUser, setClassesByUser] = useState({});
  const [expandedUsers, setExpandedUsers] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [certTypeFilter, setCertTypeFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const agencyManagementEnabled = isFeatureEnabled("agencyManagement");

  // Fetch agencies for this tenant
  useEffect(() => {
    if (!activeTenantId) return;
    fetchAgencies(activeTenantId).then((list) => {
      const arr = Array.isArray(list) ? list : list?.content || [];
      setAgencies(arr);
      const initial = defaultAgencyId(arr, user);
      if (initial) {
        setSelectedAgencyId(initial);
      } else {
        // No agencies found - ensure loading is set to false
        setLoading(false);
      }
    });
    fetchAllowedCertifications(activeTenantId).then(setAllowedTypes);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  const reload = useCallback(async () => {
    if (!activeTenantId || !selectedAgencyId) return;
    setLoading(true);
    try {
      const mems = await fetchAgencyMemberships(
        activeTenantId,
        selectedAgencyId,
      );
      const approved = mems.filter((m) => m.status === "APPROVED" && m.user);
      setApprovedMemberships(approved);
      setMembers(approved.map((m) => m.user));
      setPendingRequests(mems.filter((m) => m.status !== "APPROVED"));

      if (approved.length) {
        const allCerts = await fetchCertificationsByUserIds(
          approved.map((m) => m.user.id),
          activeTenantId,
        );
        const byUser = {};
        approved.forEach((m) => {
          byUser[m.user.id] = [];
        });
        allCerts.forEach((c) => {
          if (byUser[c.userId] !== undefined) byUser[c.userId].push(c);
        });
        setCertsByUser(byUser);

        const classesMap = {};
        const now = new Date();
        await Promise.all(
          approved.map(async (m) => {
            const uid = m.user.id;
            const regs = await fetchRegistrationsForUser(uid, activeTenantId);
            const upcoming = [];
            for (const r of regs) {
              const cls = await fetchClassById(r.classId);
              if (!cls) continue;
              const isFutureOrCurrent = (() => {
                if (cls.schedule && cls.schedule.length) {
                  return cls.schedule.some((s) => {
                    const end = s.end ? new Date(s.end) : new Date(s.start);
                    return end >= now;
                  });
                }
                return new Date(cls.date) >= now;
              })();
              if (isFutureOrCurrent) upcoming.push(cls);
            }
            classesMap[uid] = upcoming;
          }),
        );
        setClassesByUser(classesMap);
      } else {
        setCertsByUser({});
        setClassesByUser({});
      }
    } finally {
      setLoading(false);
    }
  }, [activeTenantId, selectedAgencyId]);

  useEffect(() => {
    if (selectedAgencyId) {
      reload();
    }
  }, [selectedAgencyId, reload]);

  if (!agencyManagementEnabled) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="info">
              Agency management is not enabled for your tenant.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (
    !user ||
    !user.roles?.some((r) =>
      ["AGENCY_ADMIN", "GLOBAL_ADMIN", "ADMIN"].includes(r),
    )
  ) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Card>
          <CardContent>
            <Alert severity="error">
              You do not have permission to view this page.
            </Alert>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (!activeTenantId) {
    return (
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <NoTenantSelected message="Select a training center from the top bar to view member compliance." />
      </Container>
    );
  }

  function getCertForType(userId, certType) {
    return findCertForType(certsByUser[userId] || [], certType);
  }

  function missingRequired(userId) {
    const membership = approvedMemberships.find((am) => am.user?.id === userId);
    return allowedTypes.filter(
      (ct) =>
        membership?.requiredCertIds?.includes(ct.id) &&
        certStatus(getCertForType(userId, ct)) === "missing",
    );
  }

  const allCertsFlat = Object.values(certsByUser).flat();
  const expiringSoon = allCertsFlat.filter(
    (c) => certStatus(c) === "expiring",
  ).length;
  const expired = allCertsFlat.filter(
    (c) => certStatus(c) === "expired",
  ).length;

  let filteredMembers = members;
  if (statusFilter === "expiring") {
    filteredMembers = members.filter((m) =>
      (certsByUser[m.id] || []).some((c) => certStatus(c) === "expiring"),
    );
  } else if (statusFilter === "expired") {
    filteredMembers = members.filter((m) =>
      (certsByUser[m.id] || []).some((c) => certStatus(c) === "expired"),
    );
  } else if (statusFilter === "issues") {
    filteredMembers = members.filter(
      (m) =>
        (certsByUser[m.id] || []).some(
          (c) => certStatus(c) === "expiring" || certStatus(c) === "expired",
        ) || missingRequired(m.id).length > 0,
    );
  }

  const displayedCertTypes =
    certTypeFilter === "all"
      ? allowedTypes
      : allowedTypes.filter((a) => a.id === certTypeFilter);


  async function handleRemoveMember(userId) {
    await updateAgencyMembership(activeTenantId, selectedAgencyId, userId, {
      status: "REMOVED",
    });
    reload();
  }

  const summaryStats = [
    { label: "Total Members", value: members.length, color: "primary" },
    {
      label: "Expiring Within 30 Days",
      value: expiringSoon,
      color: expiringSoon > 0 ? "warning" : "success",
    },
    {
      label: "Expired Certifications",
      value: expired,
      color: expired > 0 ? "error" : "success",
    },
    { label: "Cert Types Tracked", value: allowedTypes.length, color: "info" },
  ];

  return (
    <>
      <Box
        sx={{
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "grey.200",
          mb: 3,
        }}
      >
        <Container maxWidth="xl" sx={{ py: { xs: 3, md: 4 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate("/agency")}
            sx={{
              color: "text.secondary",
              mb: 2,
              textTransform: "none",
              fontWeight: 500,
            }}
          >
            Back to Agency Management
          </Button>
          <Typography
            variant="h4"
            sx={{ fontWeight: 600, mb: 1, fontFamily: "var(--font-display)" }}
          >
            Member Compliance
          </Typography>
          <Typography color="text.secondary">
            View member certifications and verify compliance with role
            requirements
          </Typography>
        </Container>
      </Box>
      <Container maxWidth="xl" sx={{ pb: 4 }}>
        {canPickAgency(user) && (
          <>
            {agencies.length > 1 && (
              <FormControl size="small" sx={{ mb: 3, minWidth: 260 }}>
                <InputLabel>Agency</InputLabel>
                <Select
                  value={selectedAgencyId || ""}
                  label="Agency"
                  onChange={(e) => {
                    setSelectedAgencyId(e.target.value);
                  }}
                >
                  {agencies.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            {agencies.length === 1 && (
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 3 }}>
                {agencies[0].name}
              </Typography>
            )}
          </>
        )}

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && (
          <>
            {/* ── Info alert ── */}
            <Alert severity="info" sx={{ mb: 3 }}>
              To invite members or approve pending applications, go to{" "}
              <strong>Agency Management</strong>.
            </Alert>

            <Divider sx={{ mb: 3 }} />

            {/* ── Summary cards ── */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {summaryStats.map((s) => (
                <Grid item xs={6} sm={3} key={s.label}>
                  <Card
                    sx={{
                      textAlign: "center",
                      borderTop: 4,
                      borderColor: `${s.color}.main`,
                    }}
                  >
                    <CardContent>
                      <Typography
                        variant="h4"
                        color={`${s.color}.main`}
                        fontWeight="bold"
                        sx={{ fontFamily: "var(--font-display)" }}
                      >
                        {s.value}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {s.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* ── Filters ── */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mr: 1 }}
                  >
                    Show:
                  </Typography>
                  {[
                    { key: "all", label: "All Members" },
                    { key: "issues", label: "⚠ Any Issues" },
                    { key: "expiring", label: "Expiring Soon" },
                    { key: "expired", label: "Expired" },
                  ].map((f) => (
                    <Chip
                      key={f.key}
                      label={f.label}
                      onClick={() => setStatusFilter(f.key)}
                      color={statusFilter === f.key ? "primary" : "default"}
                      variant={statusFilter === f.key ? "filled" : "outlined"}
                      size="small"
                    />
                  ))}

                  <FormControl size="small" sx={{ minWidth: 160, ml: 1 }}>
                    <InputLabel>Cert Type</InputLabel>
                    <Select
                      value={certTypeFilter}
                      label="Cert Type"
                      onChange={(e) => setCertTypeFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      {allowedTypes.map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </CardContent>
            </Card>

            {/* ── Member × Cert compliance table ── */}
            <Card>
              <CardContent>
                {members.length === 0 ? (
                  <Typography color="text.secondary">
                    No approved members yet. Approve pending requests or invite
                    members above.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow
                          sx={{
                            "& th": {
                              fontWeight: "bold",
                              bgcolor: "action.hover",
                            },
                          }}
                        >
                          <TableCell />
                          <TableCell>Member</TableCell>
                          <TableCell>Category</TableCell>
                          <TableCell>Class Count</TableCell>
                          <TableCell>Required Certs</TableCell>
                          <TableCell align="center">Actions</TableCell>
                          {displayedCertTypes.map((ct) => (
                            <TableCell
                              key={ct.id}
                              align="center"
                              sx={{ minWidth: 130 }}
                            >
                              {ct.name}
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                {ct.issuer}
                              </Typography>
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredMembers.map((m) => {
                          const membership = approvedMemberships.find(
                            (am) => am.user?.id === m.id,
                          );
                          const userClasses = classesByUser[m.id] || [];
                          return (
                            <React.Fragment key={m.id}>
                              <TableRow hover>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      setExpandedUsers((prev) => ({
                                        ...prev,
                                        [m.id]: !prev[m.id],
                                      }))
                                    }
                                  >
                                    {expandedUsers[m.id] ? "▾" : "▸"}
                                  </IconButton>
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    fontWeight="medium"
                                  >
                                    {m.fullName || m.name || m.username}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    {m.email}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {membership?.category || (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>{userClasses.length}</TableCell>
                                <TableCell>
                                  {(membership?.requiredCertIds || [])
                                    .length === 0 ? (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      None set
                                    </Typography>
                                  ) : (
                                    (membership.requiredCertIds || [])
                                      .map((id) => {
                                        const ct = allowedTypes.find(
                                          (a) => a.id === id,
                                        );
                                        return ct ? ct.name : id;
                                      })
                                      .join(", ")
                                  )}
                                </TableCell>
                                <TableCell align="center">
                                  <Tooltip title="Remove member">
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleRemoveMember(m.id)}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </TableCell>
                                {displayedCertTypes.map((ct) => {
                                  const cert = getCertForType(m.id, ct);
                                  const status = certStatus(cert);
                                  const { color, label } = STATUS_CFG[status];
                                  const days = cert
                                    ? daysUntil(cert.expires)
                                    : null;

                                  let chipLabel;
                                  if (status === "valid")
                                    chipLabel = `✓ ${fmtDate(cert.expires)}`;
                                  else if (status === "expiring")
                                    chipLabel = `⚠ ${days}d left`;
                                  else if (status === "expired")
                                    chipLabel = "Expired";
                                  else chipLabel = "—";

                                  const tooltipText = cert
                                    ? `${label} · #${cert.certNumber || "N/A"} · Expires ${fmtDate(cert.expires)}`
                                    : "Certification not on file";

                                  const isRequired =
                                    membership?.requiredCertIds?.includes(
                                      ct.id,
                                    );
                                  const needsAttention =
                                    isRequired &&
                                    (status === "missing" ||
                                      status === "expired" ||
                                      status === "expiring");

                                  return (
                                    <TableCell
                                      key={ct.id}
                                      align="center"
                                      sx={
                                        needsAttention
                                          ? { bgcolor: (t) => alpha(t.palette.error.main, 0.08) }
                                          : undefined
                                      }
                                    >
                                      <Tooltip title={tooltipText}>
                                        <Chip
                                          label={isRequired && status === "missing" ? "Missing" : chipLabel}
                                          color={isRequired && status === "missing" ? "error" : color}
                                          size="small"
                                          variant={
                                            status === "missing"
                                              ? "outlined"
                                              : "filled"
                                          }
                                        />
                                      </Tooltip>
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                              <TableRow>
                                <TableCell
                                  style={{ paddingBottom: 0, paddingTop: 0 }}
                                  colSpan={displayedCertTypes.length + 5}
                                >
                                  <Collapse
                                    in={expandedUsers[m.id]}
                                    timeout="auto"
                                    unmountOnExit
                                  >
                                    <Box margin={1}>
                                      <Typography variant="subtitle2">
                                        Upcoming/Current Classes
                                      </Typography>
                                      {userClasses.length > 0 ? (
                                        <List dense>
                                          {userClasses.map((c) => (
                                            <ListItem key={c.id} disableGutters>
                                              <ListItemText
                                                primary={c.title}
                                                secondary={fmtDate(c.date)}
                                              />
                                            </ListItem>
                                          ))}
                                        </List>
                                      ) : (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          none
                                        </Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })}
                        {filteredMembers.length === 0 && (
                          <TableRow>
                            <TableCell
                              colSpan={displayedCertTypes.length + 3}
                              align="center"
                            >
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ py: 2 }}
                              >
                                No members match the current filter.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Container>
    </>
  );
}
